import React from 'react'
import { Box, Checkbox, FormControlLabel, TextField } from '@mui/material'
import moment from 'moment'
import QuickDurationSelection from '../Buttons/QuickDurationSelection'
import TimeSelect from './TimeSelect'

const FORMAT = 'YYYY-MM-DDTHH:mm'
const DATE = 'YYYY-MM-DD'

const DURATIONS = [
  { value: 30, label: '30min' },
  { value: 45, label: '45min' },
  { value: 60, label: '1h' },
  { value: 90, label: '1,5h' },
  { value: 120, label: '2h' },
  { value: 180, label: '3h' },
]

/**
 * Day + start + end for an activity, with duration shortcuts.
 *
 * Replaces a pair of datetime-local inputs, which made the user re-pick the date for
 * the end even though an activity almost always starts and ends on one day. When it
 * genuinely does not, "Päättyy eri päivänä" reveals an end date rather than the end
 * time silently rolling over.
 */
function ActivityTimeRange({ start, end, onChange }) {
  const startMoment = start ? moment(start, FORMAT) : null
  const endMoment = end ? moment(end, FORMAT) : null
  const startValid = !!(startMoment && startMoment.isValid())
  const endValid = !!(endMoment && endMoment.isValid())

  const date = startValid ? startMoment.format(DATE) : ''
  const startTime = startValid ? startMoment.format('HH:mm') : ''
  const endDate = endValid ? endMoment.format(DATE) : date
  const endTime = endValid ? endMoment.format('HH:mm') : ''
  const duration = startValid && endValid ? endMoment.diff(startMoment, 'minutes') : null

  const differentDay = !!(date && endDate && endDate !== date)
  const invalidRange = startValid && endValid && !endMoment.isAfter(startMoment)

  const emit = (nextStart, nextEnd) =>
    onChange({
      start: nextStart ? nextStart.format(FORMAT) : '',
      end: nextEnd ? nextEnd.format(FORMAT) : '',
    })

  const handleDate = (value) => {
    if (!value) return
    const next = moment(value + 'T' + (startTime || '12:00'), FORMAT)
    // Keep the length, so moving the day never silently resizes the activity. A
    // cross-midnight activity stays cross-midnight for the same reason.
    emit(next, duration !== null ? next.clone().add(duration, 'minutes') : null)
  }

  const handleStartTime = (value) => {
    const next = moment((date || moment().format(DATE)) + 'T' + value, FORMAT)
    emit(next, duration !== null ? next.clone().add(duration, 'minutes') : null)
  }

  const handleEndTime = (value) => {
    if (!startValid) return
    emit(startMoment, moment((endDate || date) + 'T' + value, FORMAT))
  }

  const handleEndDate = (value) => {
    if (!value || !startValid) return
    emit(startMoment, moment(value + 'T' + (endTime || startTime), FORMAT))
  }

  const toggleDifferentDay = (checked) => {
    if (!startValid) return
    const base = moment(date + 'T' + (endTime || startTime), FORMAT)
    emit(startMoment, checked ? base.add(1, 'day') : base)
  }

  const handleDuration = (minutes) => {
    if (!startValid) return
    emit(startMoment, startMoment.clone().add(minutes, 'minutes'))
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          required
          id='activity-date'
          label='Päivä'
          type='date'
          size='small'
          sx={{ flex: '0 1 190px' }}
          value={date}
          onChange={(e) => handleDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TimeSelect
          id='activity-starttime'
          label='Alkaa'
          value={startTime}
          onChange={handleStartTime}
          sx={{ flex: '0 0 130px' }}
        />
        {/* The end date comes first so the row reads in the order it is spoken:
            starts on this day at this time, ends on that day at that time. */}
        {differentDay ? (
          <TextField
            required
            id='activity-enddate'
            label='Päättymispäivä'
            type='date'
            size='small'
            sx={{ flex: '0 1 190px' }}
            value={endDate}
            onChange={(e) => handleEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        ) : null}
        <TimeSelect
          id='activity-endtime'
          label='Päättyy'
          value={endTime}
          onChange={handleEndTime}
          sx={{ flex: '0 0 130px' }}
          error={invalidRange}
          helperText={invalidRange ? 'Loppuaika on ennen alkuaikaa' : undefined}
        />
      </Box>
      <Box sx={{ mt: 0.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <QuickDurationSelection options={DURATIONS} value={duration} onChange={handleDuration} />
        <FormControlLabel
          control={
            <Checkbox
              size='small'
              checked={differentDay}
              disabled={!startValid}
              onChange={(e) => toggleDifferentDay(e.target.checked)}
            />
          }
          label='Päättyy eri päivänä'
        />
      </Box>
    </Box>
  )
}

export default ActivityTimeRange
